import type {
  OpportunityRelatedBundledExperience,
  OpportunityRelatedDeal,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import { BASIC_CATEGORY_INDEX, MAX_LOS, MIN_LOS, PACKAGE_CATEGORIES } from './packageCategories';
import { defaultRoomPricing, deriveExtraNightSell, newPackageOptionId } from './packageOptionsModel';
import type { CategoryRoom, OptionState, RoomAssetLink, RoomPricing } from './types';

export type PreviousRoomPricing = {
  asset: Required<RoomAssetLink>;
  occupancy: string;
  allocation: string;
  sellByLos: Record<number, number>;
  netByLos: Record<number, number>;
  extraNightSell?: number;
  extraNightNet?: number;
};

export type PreviousDealOptions = {
  rooms: Array<PreviousRoomPricing>;
  ambiguousCells: number;
  mixedOccupancyRooms: number;
  otherCurrencyNetCells: number;
};

type PreviousRoomDeals = {
  asset: Required<RoomAssetLink>;
  dealsByLos: Record<number, Array<OpportunityRelatedDeal>>;
};

const { defaultOccupancy: DEFAULT_OCCUPANCY } = PACKAGE_CATEGORIES[BASIC_CATEGORY_INDEX];
const OCCUPANCY = /\b[1-9]\s*a(\s*[+&,]?\s*[0-9]\s*[cti])*(?![a-z0-9])/i;
const SOLO_WORDS = /\b(solo|single|one\s+(adult|person|guest)|1\s+(adult|person|guest))\b/i;
const SEVERAL_ADULTS_WORDS = /\b[2-9]\s+adults?\b/i;

// Salesforce's occupancy fields are almost never filled, so it is read from the Deal Option name. An explicit
// one ("2A", "1A 1C", "2A + 1C") always decides; "single" also names bedding ("2 Single Beds 2A"), so solo
// words only count when there is none.
function occupancyOf(name: string | null | undefined): string | undefined {
  const explicit = name
    ?.match(OCCUPANCY)?.[0]
    .replace(/[^0-9a-z]/gi, '')
    .toUpperCase();
  if (explicit) {
    return explicit;
  }
  return name && SOLO_WORDS.test(name) && !SEVERAL_ADULTS_WORDS.test(name) ? '1A' : undefined;
}

// The grid holds one occupancy per room, so a room whose Deal Options name different ones keeps the default.
function roomOccupancy(deals: Array<OpportunityRelatedDeal>): string | undefined {
  const occupancies = new Set(deals.map((deal) => occupancyOf(deal.name) ?? DEFAULT_OCCUPANCY));
  return occupancies.size === 1 ? [...occupancies][0] : undefined;
}

function positiveNumber(value: unknown): number | undefined {
  const number = Number(value);
  return number > 0 ? number : undefined;
}

// Salesforce keeps sell in AUD but cost in the Deal Option's own currency, so net is only copied when
// that currency is the one this offer's net prices are entered in.
function toPreviousRoomPricing(
  asset: Required<RoomAssetLink>,
  occupancy: string,
  dealByLos: Record<number, OpportunityRelatedDeal>,
  netCurrency: string,
): PreviousRoomPricing {
  const lengthsOfStay = Object.keys(dealByLos).map(Number);
  const sellByLos: Record<number, number> = {};
  const netByLos: Record<number, number> = {};
  const netOf = (deal: OpportunityRelatedDeal, field: 'cost_price__c' | 'nightly_cost_price__c') =>
    deal.currencyisocode === netCurrency ? positiveNumber(deal[field]) : undefined;

  for (const nights of lengthsOfStay) {
    const deal = dealByLos[nights];
    sellByLos[nights] = Number(deal.price__c);
    const net = netOf(deal, 'cost_price__c');
    if (net !== undefined) {
      netByLos[nights] = net;
    }
  }

  // The tool holds one extra night price per room; it derives it from the shortest stay, so copy that one's.
  const shortestStayDeal = dealByLos[Math.min(...lengthsOfStay)];

  return {
    asset,
    occupancy,
    allocation: String(shortestStayDeal.allocation__c ?? '').trim(),
    sellByLos,
    netByLos,
    extraNightSell: positiveNumber(shortestStayDeal.nightly_sell_price__c),
    extraNightNet: netOf(shortestStayDeal, 'nightly_cost_price__c'),
  };
}

// Bundled Deal Options are derived from a base one by Salesforce's Bundled Experiences flow, so they
// belong to package upgrades, not to the base grid. Any other room and LOS with several Deal Options
// (occupancies, solo, upgrades) is left out: only its name tells them apart, and the grid holds one.
export function reducePreviousDealOptions(
  deals: Array<OpportunityRelatedDeal> = [],
  bundledExperiences: Array<OpportunityRelatedBundledExperience> = [],
  netCurrency: string,
): PreviousDealOptions {
  const bundledDealIds = new Set(bundledExperiences.map((experience) => experience.deal_option__c).filter(Boolean));
  const roomsBySfid = new Map<string, PreviousRoomDeals>();

  for (const deal of deals) {
    const sfid = deal.room_asset?.sfid;
    const name = deal.room_asset?.name;
    const nights = Number(deal.number_of_nights__c);
    const sell = Number(deal.price__c);

    if (bundledDealIds.has(String(deal.id)) || !sfid || !name) {
      continue;
    }
    if (!Number.isInteger(nights) || nights < MIN_LOS || nights > MAX_LOS || !(sell > 0)) {
      continue;
    }

    const room = roomsBySfid.get(sfid) ?? { asset: { id: sfid, sfid, name }, dealsByLos: {} };
    room.dealsByLos[nights] = [...(room.dealsByLos[nights] ?? []), deal];
    roomsBySfid.set(sfid, room);
  }

  const rooms: Array<PreviousRoomPricing> = [];
  let ambiguousCells = 0;
  let mixedOccupancyRooms = 0;
  let otherCurrencyNetCells = 0;

  for (const { asset, dealsByLos } of roomsBySfid.values()) {
    const dealByLos: Record<number, OpportunityRelatedDeal> = {};
    for (const [nights, cellDeals] of Object.entries(dealsByLos)) {
      if (cellDeals.length === 1) {
        dealByLos[Number(nights)] = cellDeals[0];
      } else {
        ambiguousCells += 1;
      }
    }
    if (Object.keys(dealByLos).length === 0) {
      continue;
    }
    const occupancy = roomOccupancy(Object.values(dealByLos));
    if (occupancy === undefined) {
      mixedOccupancyRooms += 1;
    }
    otherCurrencyNetCells += Object.values(dealByLos).filter(
      (deal) => deal.currencyisocode !== netCurrency && positiveNumber(deal.cost_price__c) !== undefined,
    ).length;
    rooms.push(toPreviousRoomPricing(asset, occupancy ?? DEFAULT_OCCUPANCY, dealByLos, netCurrency));
  }

  return { rooms, ambiguousCells, mixedOccupancyRooms, otherCurrencyNetCells };
}

export function previousLengthsOfStay({ rooms }: PreviousDealOptions): Array<number> {
  return [...new Set(rooms.flatMap((previous) => Object.keys(previous.sellByLos).map(Number)))].sort((a, b) => a - b);
}

function copiedRoomPricing(previous: PreviousRoomPricing, lengthsOfStay: Array<number>): RoomPricing {
  const pricing: RoomPricing = {
    ...defaultRoomPricing(previous.occupancy),
    allocation: previous.allocation,
    extraNightNet: previous.extraNightNet === undefined ? '' : String(previous.extraNightNet),
  };

  for (const nights of lengthsOfStay) {
    if (previous.sellByLos[nights] !== undefined) {
      pricing.sellByLos[nights] = String(previous.sellByLos[nights]);
      pricing.sellEditedByLos[nights] = true;
    }
    if (previous.netByLos[nights] !== undefined) {
      pricing.netByLos[nights] = String(previous.netByLos[nights]);
    }
  }

  if (previous.extraNightSell !== undefined) {
    return { ...pricing, extraNightSell: String(previous.extraNightSell), extraNightSellEdited: true };
  }
  return { ...pricing, extraNightSell: deriveExtraNightSell(pricing, lengthsOfStay) };
}

// A room already on the same asset keeps its id, so inclusions applied to it stay attached.
export function replaceWithPreviousRoomPricing(state: OptionState, previous: PreviousDealOptions): OptionState {
  const lengthsOfStay = previousLengthsOfStay(previous);
  const existingRooms = state.roomsByCategory[BASIC_CATEGORY_INDEX];
  const rooms: Array<CategoryRoom> = [];
  const pricingByRoomId: Record<string, RoomPricing> = {};

  for (const previousRoom of previous.rooms) {
    const { asset } = previousRoom;
    const room: CategoryRoom = {
      id: existingRooms.find((existing) => existing.assetId === asset.id)?.id ?? newPackageOptionId(),
      assetId: asset.id,
      assetName: asset.name,
      packageName: asset.name,
      asiaOnly: false,
    };

    rooms.push(room);
    pricingByRoomId[room.id] = copiedRoomPricing(previousRoom, lengthsOfStay);
  }

  return {
    ...state,
    lengthsOfStay,
    selectedLos: lengthsOfStay[0],
    pricingByRoomId,
    roomsByCategory: state.roomsByCategory.map((_, index) => (index === BASIC_CATEGORY_INDEX ? rooms : [])),
    netRateFormula: { ...state.netRateFormula, selectedRoomIds: [] },
  };
}
